import { type JSX } from 'react';

import { CheckCircleFilled, ClockCircleOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { Col, Row, Tabs } from 'antd';
import { isUndefined } from 'lodash';
import MediaQuery from 'react-responsive';
import { useLocation, useNavigate } from 'react-router';
import { useAccountSelector } from 'src/hooks';
import { MDivider } from 'src/lib/MDivider/MDivider';
import { AccountType, JointAccountType } from 'src/models';
import {
  isAccountAgreementsComplete,
  isAccountDisclosureComplete,
  isAccountFinancialInformationComplete,
  isAccountPersonalInformationComplete,
  isAccountSuitabilityInformationComplete,
  isAccountTrustedContactComplete,
} from 'src/utils';

import { Color, ScreenBreakpoint, Spacing } from '../../../styles';
import { Logger } from '../../../utils/Logger';
import { MTypography } from '../../MTypography/MTypography';

import { MyAccountProgressBar } from './MyAccountProgressBar';
import * as Styles from './MyAccountSidebar.styles';

export enum MyAccountSidebarMainMenuItemKey {
  EntityInformation = 'EntityInformation',
  PersonalInformation = 'PersonalInformation',
  TrustedContact = 'TrustedContact',
  SuitabilityInformation = 'SuitabilityInformation',
  FinancialInformation = 'FinancialInformation',
  Disclosures = 'Disclosures',
  AccountAgrements = 'AccountAgreements',
  SignApplication = 'SignApplication',
}

export enum MyAccountSidebarOtherMenuItemKey {
  MoneyTransfers = 'MoneyTransfers',
  AccountStatus = 'AccountStatus',
  AccountStatements = 'AccountStatements',
  IndicationsOfInterest = 'IndicationsOfInterest',
  UserSettings = 'UserSettings',
}

export interface MyAccountSidebarMainMenuItem {
  key: MyAccountSidebarMainMenuItemKey;
  name: (param: {
    type?: AccountType;
    jointAccountType?: JointAccountType;
    isEntityAccountTypeActive?: boolean;
  }) => string;
  path: (accountId?: string) => string;
  icon?: JSX.Element | null;
  isVisible: (param: { type?: AccountType; isEntityAccountTypeActive?: boolean }) => boolean;
  isOptional?: (param: { type?: AccountType; jointAccountType?: JointAccountType }) => boolean;
}

export interface MyAccountSidebarOtherMenuItem {
  key: MyAccountSidebarOtherMenuItemKey;
  name: (param: {
    type?: AccountType;
    jointAccountType?: JointAccountType;
    isEntityAccountTypeActive?: boolean;
  }) => string;
  path: (accountId: string) => string;
  isVisible: (param: {
    type?: AccountType;
    isEntityAccountTypeActive?: boolean;
    lastFailedLinkAttemptError?: string;
  }) => boolean;
  isOptional?: (param: { type?: AccountType; jointAccountType?: JointAccountType }) => boolean;
}

export type MyAccountSidebarMenuItemKey = MyAccountSidebarMainMenuItemKey | MyAccountSidebarOtherMenuItemKey;

const completedAccountMenuItems: MyAccountSidebarMainMenuItem[] = [
  {
    key: MyAccountSidebarMainMenuItemKey.EntityInformation,
    name: () => 'Entity',
    path: () => '/accounts/entity-information',
    isVisible: ({ type, isEntityAccountTypeActive = false }) => (type?.isEntity ? isEntityAccountTypeActive : false),
    isOptional: () => false,
  },
  {
    key: MyAccountSidebarMainMenuItemKey.PersonalInformation,
    name: ({ type, isEntityAccountTypeActive }) => {
      if (isUndefined(type)) {
        return 'Account Type';
      }

      if (type.isEntity && !isEntityAccountTypeActive) {
        return 'Entity';
      }

      return 'Personal';
    },
    path: () => `/accounts/personal-information`,
    isVisible: () => true,
  },
  {
    key: MyAccountSidebarMainMenuItemKey.TrustedContact,
    name: ({ type, jointAccountType }) => {
      if (type?.isJoint && jointAccountType?.isTenantsInCommon) {
        return 'User / Trusted Contact';
      }

      if (type?.isRetirement) {
        return 'Trusted Contact / Beneficiary (Optional)';
      }

      return 'Trusted Contact Person (Optional)';
    },
    path: () => `/accounts/trusted-contact`,
    isVisible: ({ type, isEntityAccountTypeActive = false }) => (type?.isEntity ? isEntityAccountTypeActive : true),
    isOptional: ({ type, jointAccountType }) => (type?.isJoint && jointAccountType?.isTenantsInCommon ? false : true),
  },
  {
    key: MyAccountSidebarMainMenuItemKey.SuitabilityInformation,
    name: () => 'Suitability Information',
    path: () => `/accounts/suitability-information`,
    isVisible: ({ type, isEntityAccountTypeActive = false }) => (type?.isEntity ? isEntityAccountTypeActive : true),
  },
  {
    key: MyAccountSidebarMainMenuItemKey.FinancialInformation,
    name: () => 'Financial Information',
    path: () => `/accounts/financial-information`,
    isVisible: ({ type, isEntityAccountTypeActive = false }) => (type?.isEntity ? isEntityAccountTypeActive : true),
  },
  {
    key: MyAccountSidebarMainMenuItemKey.Disclosures,
    name: () => 'Disclosures',
    path: () => `/accounts/disclosures`,
    isVisible: ({ type, isEntityAccountTypeActive = false }) => (type?.isEntity ? isEntityAccountTypeActive : true),
  },
];

const incompleteAccountMenuItems: MyAccountSidebarMainMenuItem[] = completedAccountMenuItems.concat([
  {
    key: MyAccountSidebarMainMenuItemKey.AccountAgrements,
    name: () => 'Account Agreements',
    path: () => `/accounts/account-agreements`,
    isVisible: ({ type }) => !type?.isEntity,
  },
  {
    key: MyAccountSidebarMainMenuItemKey.SignApplication,
    name: () => 'Sign Application',
    path: () => `/accounts/sign-application`,
    isVisible: ({ type }) => !type?.isEntity,
  },
]);

const accountSettingMenuItemList: MyAccountSidebarOtherMenuItem[] = [
  {
    key: MyAccountSidebarOtherMenuItemKey.MoneyTransfers,
    name: () => 'Money Transfers & Preferences',
    path: (accountId: string) => `/accounts/${accountId}/money-transfers`,
    isVisible: ({ type, isEntityAccountTypeActive = false, lastFailedLinkAttemptError }) =>
      type?.isEntity ? isEntityAccountTypeActive : !Boolean(lastFailedLinkAttemptError),
  },
  {
    key: MyAccountSidebarOtherMenuItemKey.AccountStatus,
    name: () => 'Account Status',
    path: (accountId: string) => `/accounts/${accountId}/account-status`,
    isVisible: () => true,
  },
  {
    key: MyAccountSidebarOtherMenuItemKey.AccountStatements,
    name: () => 'Statements & Documents',
    path: (accountId: string) => `/accounts/${accountId}/statements`,
    isVisible: ({ type, isEntityAccountTypeActive = false, lastFailedLinkAttemptError }) =>
      type?.isEntity ? isEntityAccountTypeActive : !Boolean(lastFailedLinkAttemptError),
  },
];

const userSettingMenuItemList: MyAccountSidebarOtherMenuItem[] = [
  {
    key: MyAccountSidebarOtherMenuItemKey.UserSettings,
    name: () => 'Change Email Address & Phone Number',
    path: () => `/user/settings`,
    isVisible: ({ type, isEntityAccountTypeActive = false }) => (type?.isEntity ? isEntityAccountTypeActive : true),
  },
  {
    key: MyAccountSidebarOtherMenuItemKey.IndicationsOfInterest,
    name: () => 'Indications of Interest',
    path: () => `/user/indications-of-interest`,
    isVisible: () => true,
  },
];

export interface MyAccountSidebarProps {
  menuItemKey: MyAccountSidebarMenuItemKey;
}

export const MyAccountSidebar = ({ menuItemKey }: MyAccountSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { account } = useAccountSelector();

  const isAccountSidebarMenuItemComplete = (key: MyAccountSidebarMainMenuItemKey): boolean => {
    if (!account) {
      return false;
    }

    switch (key) {
      case MyAccountSidebarMainMenuItemKey.EntityInformation: {
        return true;
      }

      case MyAccountSidebarMainMenuItemKey.PersonalInformation: {
        return isAccountPersonalInformationComplete(account);
      }

      case MyAccountSidebarMainMenuItemKey.TrustedContact: {
        return isAccountTrustedContactComplete(account);
      }

      case MyAccountSidebarMainMenuItemKey.SuitabilityInformation: {
        return isAccountSuitabilityInformationComplete(account);
      }

      case MyAccountSidebarMainMenuItemKey.FinancialInformation: {
        return isAccountFinancialInformationComplete(account);
      }

      case MyAccountSidebarMainMenuItemKey.Disclosures: {
        return isAccountDisclosureComplete(account);
      }

      case MyAccountSidebarMainMenuItemKey.AccountAgrements: {
        return isAccountAgreementsComplete(account);
      }

      case MyAccountSidebarMainMenuItemKey.SignApplication: {
        return false;
      }
    }
  };

  const getMyAccountMenuItems = (): MyAccountSidebarMainMenuItem[] => {
    if (!account) {
      return [
        {
          ...completedAccountMenuItems[1],
          icon: completedAccountMenuItems[1].isOptional ? null : (
            <ClockCircleOutlined className={Styles.menuItemIcon({ isComplete: false })} />
          ),
        },
      ];
    }

    if (account?.accountId) {
      return completedAccountMenuItems
        .filter(aMenuItem =>
          aMenuItem?.isVisible?.({ type: account.type, isEntityAccountTypeActive: account.isEntityAccountTypeActive }),
        )
        .map(aMenuItem => ({
          ...aMenuItem,
          icon: !aMenuItem.isOptional?.({ type: account.type, jointAccountType: account.jointAccountType }) ? (
            <CheckCircleFilled className={Styles.menuItemIcon({ isComplete: true })} />
          ) : null,
        }));
    }

    return incompleteAccountMenuItems
      .filter(aMenuItem =>
        aMenuItem?.isVisible?.({ type: account.type, isEntityAccountTypeActive: account.isEntityAccountTypeActive }),
      )
      .map(aMenuItem => {
        let isComplete = false;

        try {
          isComplete = isAccountSidebarMenuItemComplete(aMenuItem.key);
        } catch (error) {
          Logger.error(error);
        }

        return {
          ...aMenuItem,
          icon: aMenuItem.isOptional?.({
            type: account.type,
            jointAccountType: account.jointAccountType,
          }) ? null : isComplete ? (
            <CheckCircleFilled className={Styles.menuItemIcon({ isComplete: true })} />
          ) : (
            <ClockCircleOutlined className={Styles.menuItemIcon({ isComplete: false })} />
          ),
        };
      });
  };

  const getAccountSettingMenuItems = (): MyAccountSidebarOtherMenuItem[] => {
    if (!account?.accountId) {
      return [];
    }

    return accountSettingMenuItemList.filter(aMenuItem =>
      aMenuItem.isVisible?.({
        type: account.type,
        isEntityAccountTypeActive: account.isEntityAccountTypeActive,
        lastFailedLinkAttemptError: account.lastFailedLinkAttemptError,
      }),
    );
  };

  const getUserSettingMenuItems = (): MyAccountSidebarOtherMenuItem[] => {
    if (!account?.accountId) {
      return [
        {
          key: MyAccountSidebarOtherMenuItemKey.IndicationsOfInterest,
          name: () => 'Indications of Interest',
          path: () => `/user/indications-of-interest`,
          isVisible: () => true,
        },
      ];
    }

    return userSettingMenuItemList.filter(aMenuItem =>
      aMenuItem?.isVisible?.({ type: account.type, isEntityAccountTypeActive: account.isEntityAccountTypeActive }),
    );
  };

  const getProgressBarSteps = () => {
    if (!account) {
      return [];
    }

    if (account?.accountId) {
      return [];
    }

    return incompleteAccountMenuItems
      .filter(
        aMenuItem =>
          !aMenuItem.isOptional?.({
            type: account?.type,
            jointAccountType: account?.jointAccountType,
          }) && aMenuItem.isVisible?.({ type: account?.type }),
      )
      .map(aMenuItem => ({
        isComplete: isAccountSidebarMenuItemComplete(aMenuItem.key),
      }));
  };

  const renderAccountMenuItems = () => {
    return getMyAccountMenuItems().map(aMenuItem => {
      return (
        <Col
          key={aMenuItem.key}
          span={22}
          className={Styles.menuItem({ isActive: aMenuItem.key === menuItemKey })}
          onClick={() => {
            navigate(aMenuItem.path(account?.id));
          }}>
          <div data-testid={'account-' + aMenuItem.key}>
            {aMenuItem.name({
              type: account?.type,
              jointAccountType: account?.jointAccountType,
              isEntityAccountTypeActive: account?.isEntityAccountTypeActive,
            })}
          </div>
          <div className={Styles.menuItemIconContainer}>{aMenuItem.icon}</div>
        </Col>
      );
    });
  };

  const renderAccountSettings = () => {
    return getAccountSettingMenuItems().map(anOtherMenuItem => {
      return (
        <Col
          key={anOtherMenuItem.key}
          span={22}
          className={Styles.menuItem({ isActive: anOtherMenuItem.key === menuItemKey })}
          onClick={() => {
            navigate(anOtherMenuItem.path(account!.id));
          }}>
          <div>
            {anOtherMenuItem.name({
              type: account?.type,
              jointAccountType: account?.jointAccountType,
            })}
          </div>
        </Col>
      );
    });
  };

  const renderUserSettings = () => {
    return getUserSettingMenuItems().map(anOtherMenuItem => {
      return (
        <Col
          key={anOtherMenuItem.key}
          span={22}
          className={Styles.menuItem({ isActive: anOtherMenuItem.key === menuItemKey })}
          onClick={() => {
            navigate(anOtherMenuItem.path(account!.id));
          }}>
          <div>
            {anOtherMenuItem.name({
              type: account?.type,
              jointAccountType: account?.jointAccountType,
            })}
          </div>
        </Col>
      );
    });
  };

  const getActiveMenuItemKey = () => {
    if (account?.id) {
      const activeTab = getMyAccountMenuItems().find(aMenuItem =>
        aMenuItem.path(account?.id).includes(location.pathname),
      );

      return activeTab?.key;
    }
  };

  const getMobileTabItems = () => {
    return getMyAccountMenuItems().map(aMenuItem => {
      return {
        key: aMenuItem.key,
        label: (
          <div className={Styles.tabLabelContainer}>
            {aMenuItem.name({
              type: account?.type,
              jointAccountType: account?.jointAccountType,
              isEntityAccountTypeActive: account?.isEntityAccountTypeActive,
            })}{' '}
            {aMenuItem.icon}
          </div>
        ),
        children: null,
      };
    });
  };

  const onTabChange = (key: string) => {
    const selectedMenuItem = getMyAccountMenuItems().find(aMenuItem => aMenuItem.key === key);

    if (!selectedMenuItem) {
      throw new Error(
        `Unable to find selected menu item. Expected type "MyAccountSidebarMainMenuItemKey". Received key: ${key}`,
      );
    }

    navigate(selectedMenuItem.path(account!.id));
  };

  const isAnyMainMenuItemActive = () => {
    if (!account) {
      return false;
    }

    if (account.id) {
      return incompleteAccountMenuItems.some((aMenuItem: MyAccountSidebarMainMenuItem) =>
        aMenuItem.path(account.id).includes(location.pathname),
      );
    }

    return true;
  };

  return (
    <Col className={Styles.mainContainer} md={8} sm={24} xs={24}>
      <MediaQuery maxWidth={ScreenBreakpoint.mobile.max}>
        {isAnyMainMenuItemActive() && (
          <>
            <Row>
              <Col span={22} className={Styles.pageName}>
                <MTypography variant='h4' weight='normal' color={Color.Brand6}>
                  My Account
                </MTypography>
              </Col>
            </Row>
            <Row>
              <MyAccountProgressBar steps={getProgressBarSteps()} />
            </Row>
            <Row>
              <Col span={24} className={Styles.tabsContainer}>
                <Tabs
                  items={getMobileTabItems()}
                  onChange={onTabChange}
                  type='line'
                  centered
                  activeKey={getActiveMenuItemKey()}
                />
              </Col>
            </Row>
          </>
        )}
      </MediaQuery>
      <MediaQuery minWidth={ScreenBreakpoint.laptop.min}>
        <Row>
          <Col span={22} className={Styles.pageName}>
            <MTypography variant='h4' weight='normal' color={Color.Brand6}>
              My Account
            </MTypography>
          </Col>
        </Row>
        <Row>
          <MyAccountProgressBar steps={getProgressBarSteps()} />
        </Row>
        <Row>{renderAccountMenuItems()}</Row>
        {account?.accountId && (
          <>
            <Col span={24}>
              <Row style={{ width: '90%' }} className={cx(Spacing.my0, Spacing.mxAuto)}>
                <MDivider />
              </Row>
              <Row>
                <Col span={22} className={cx(Spacing.ml8, Spacing.mt8, Spacing.mb16)}>
                  <MTypography variant='h4' weight='normal' color={Color.Brand6}>
                    Account Settings
                  </MTypography>
                </Col>
              </Row>
              <Row>{renderAccountSettings()}</Row>
            </Col>
            <Col span={24}>
              <Row style={{ width: '90%' }} className={cx(Spacing.my0, Spacing.mxAuto)}>
                <MDivider />
              </Row>
              <Row>
                <Col span={22} className={cx(Spacing.ml8, Spacing.mt8, Spacing.mb16)}>
                  <MTypography variant='h4' weight='normal' color={Color.Brand6}>
                    User Settings
                  </MTypography>
                </Col>
              </Row>
              <Row>{renderUserSettings()}</Row>
            </Col>
          </>
        )}
      </MediaQuery>
    </Col>
  );
};
